/**
 * 增强的图片组件
 * 支持响应式图片、现代格式、懒加载、错误处理等功能
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { performanceOptimizer } from '../utils/performanceOptimizer';
import { generatePlaceholder } from '../utils/placeholderGenerator';

const EnhancedImage = ({
  src,
  alt = '',
  width,
  height,
  className = '',
  style = {},
  sizes = '100vw',
  priority = false,
  quality = 85,
  placeholder = 'blur',
  onLoad,
  onError,
  fallbackType = 'article',
  ...props
}) => {
  const imgRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState('');
  const [placeholderSrc, setPlaceholderSrc] = useState('');

  // 生成响应式图片源集
  const generateSrcSet = useCallback((baseSrc) => {
    if (!baseSrc || baseSrc === null || baseSrc === undefined || baseSrc === '') return '';

    const widths = [320, 640, 768, 1024, 1280, 1920];
    const srcSet = widths.map(w => {
      const optimizedUrl = performanceOptimizer.optimizeImageUrl(baseSrc, {
        width: w,
        height: height ? Math.round((height / width) * w) : undefined,
        quality: quality,
        format: 'webp'
      });
      // 如果优化失败，返回原始URL
      return optimizedUrl ? `${optimizedUrl} ${w}w` : `${baseSrc} ${w}w`;
    }).filter(Boolean).join(', ');

    return srcSet;
  }, [width, height, quality]);

  // 生成占位图片
  useEffect(() => {
    if (placeholder === 'blur' || placeholder === 'empty') {
      const placeholderUrl = generatePlaceholder(
        width || 400,
        height || 300,
        '加载中...',
        '#f8f9fa',
        '#adb5bd'
      );
      setPlaceholderSrc(placeholderUrl);
    }
  }, [width, height, placeholder]);

  // 懒加载逻辑
  useEffect(() => {
    if (!src) return;

    const img = imgRef.current;
    if (!img) return;

    // 如果是高优先级图片，直接加载
    if (priority) {
      setCurrentSrc(src);
      return;
    }

    // 设置懒加载
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setCurrentSrc(src);
              observer.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: performanceOptimizer.isSlowNetwork() ? '10px' : '50px',
          threshold: 0.1
        }
      );

      observer.observe(img);

      return () => {
        observer.disconnect();
      };
    } else {
      // 不支持IntersectionObserver的情况下直接加载
      setCurrentSrc(src);
    }
  }, [src, priority]);

  // 处理图片加载成功
  const handleLoad = useCallback((e) => {
    setIsLoaded(true);
    setIsError(false);
    if (onLoad) onLoad(e);
  }, [onLoad]);

  // 处理图片加载失败
  const handleError = useCallback((e) => {
    setIsError(true);
    setIsLoaded(false);
    
    // 生成错误占位图片
    const errorPlaceholder = generatePlaceholder(
      width || 400,
      height || 300,
      '图片加载失败',
      '#fee2e2',
      '#dc2626'
    );
    
    e.target.src = errorPlaceholder;
    
    if (onError) onError(e);
  }, [width, height, onError]);

  // 组合className
  const combinedClassName = [
    'enhanced-image',
    className,
    isLoaded ? 'loaded' : '',
    isError ? 'error' : '',
    !isLoaded && !isError ? 'loading' : ''
  ].filter(Boolean).join(' ');

  // 如果没有图片源，显示占位图片
  if (!src || src === null || src === undefined || src === '') {
    // 生成默认占位图片
    const defaultPlaceholder = placeholderSrc || generatePlaceholder(
      width || 400,
      height || 300,
      '暂无图片',
      '#f8f9fa',
      '#adb5bd'
    );
    
    return (
      <div 
        className={`${combinedClassName} no-src`}
        style={{ 
          width: width || '100%', 
          height: height || 'auto',
          ...style 
        }}
      >
        <img
          src={defaultPlaceholder}
          alt={alt || '暂无图片'}
          width={width}
          height={height}
        />
      </div>
    );
  }

  return (
    <picture className={combinedClassName} style={style}>
      {/* WebP格式 */}
      {currentSrc && (
        <source
          srcSet={generateSrcSet(currentSrc)}
          sizes={sizes}
          type="image/webp"
        />
      )}
      
      {/* AVIF格式（如果支持） */}
      {currentSrc && performanceOptimizer.supportsAVIF() && (
        <source
          srcSet={generateSrcSet(currentSrc).replace(/format=webp/g, 'format=avif')}
          sizes={sizes}
          type="image/avif"
        />
      )}
      
      {/* 默认格式 */}
      <img
        ref={imgRef}
        src={currentSrc || placeholderSrc || generatePlaceholder(width || 400, height || 300, '加载中...', '#f8f9fa', '#adb5bd')}
        srcSet={currentSrc ? generateSrcSet(currentSrc) : ''}
        sizes={currentSrc ? sizes : ''}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    </picture>
  );
};

export default EnhancedImage; 